import { titleCase, converObjectToBlob } from 'utils';
import { ROLE_TO_SHOW, SIGNATURE_ROLE_TO_SHOW, SAMLRequestType, OIDCRequestType, MACHINERequestType, DATA_CLASSIFICATION_ROLE } from './constant';

export const getOrderPayload = ({ order, uploadedFiles = [] }) => {
    let applicationTypeValue = '';
    const isSamlRequest = order?.requestType === SAMLRequestType;
    const isOidcRequest = order?.requestType === OIDCRequestType;
    const isM2MRequest = order?.requestType === MACHINERequestType;

    if (isSamlRequest) {
        applicationTypeValue = 'Web application';
    } else if (isOidcRequest) {
        applicationTypeValue = order?.appType;
    }

    let businessJustificationValue = '';
    if (isSamlRequest && SIGNATURE_ROLE_TO_SHOW?.includes(order?.dataClassification?.toLowerCase()) && order?.isCertificateAvailable === 'yes') {
        businessJustificationValue = { certificate: order?.serviceProviderSigningCert };
    } else if (isSamlRequest) {
        businessJustificationValue = { businessJustification: order?.businessJustification };
    }

    const isExternalRequest =
        (isSamlRequest || isOidcRequest) && order?.dataClassification && order?.dataClassification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal;

    const isExternalRequestWithoutIdx = isExternalRequest && order?.isIdxAccessControlRequired === 'yes' && order?.isIdxAttributeRequired === 'no';

    const payload = {
        requestedType: order?.requestType,
        businessApplication: order?.businessApplication,
        applicationCI: order?.applicationCI,
        sector: order?.sector,
        environment: order?.environment === 'Pre-Production' ? 'Pre-Prod' : order?.environment,
        ...((isSamlRequest || isOidcRequest) && { applicationType: applicationTypeValue, dataClasification: order?.dataClassification }),
        ...(order?.authorizationRolesRequired === 'yes' &&
            ROLE_TO_SHOW?.includes(order?.dataClassification?.toLowerCase()) && { roles: order?.roles?.map((app) => app?.role) }),
        applicationName: order?.applicationName?.trim(),
        ...((isSamlRequest || isOidcRequest) && { applicationURL: order?.applicationUrl }),
        ...(isExternalRequest && { isIdxAccessControlRequired: order?.isIdxAccessControlRequired === 'yes' }),
        ...(isExternalRequestWithoutIdx && { applicationShortName: order?.idxShortName }),
        applicationOwner: order?.appOwnerDetails?.gpid,
        ...(isM2MRequest && order?.environment === 'Production' && { ssoOwner: order?.ssoOwnerDetails?.gpid }),
        authorizationRolesRequired: titleCase(order?.authorizationRolesRequired || 'no'),
        ...(isSamlRequest && {
            acsURL: order?.acsURL,
            entityId: order?.audienceURI,
            nameIdFormat: order?.nameIdFormat,
            applicationUserName: order?.applicationUserName,
        }),
        ...(isOidcRequest &&
            order?.loginUrl && {
                loginURL: order?.loginUrl,
            }),
        ...(isOidcRequest &&
            order?.isLogoutUrlRequired && {
                logoutUrlAvailable: order?.isLogoutUrlRequired === 'yes',
            }),
        ...(isOidcRequest &&
            order?.logoutUrl && {
                logoutURL: order?.logoutUrl,
            }),
        ...((isSamlRequest || isOidcRequest) && { isCertificateAvailable: titleCase(order?.isCertificateAvailable || 'no') }),
        ...businessJustificationValue,
        ...((isSamlRequest || isOidcRequest) &&
            order?.isIdxAccessControlRequired === 'no' &&
            [DATA_CLASSIFICATION_ROLE?.pepsicoInternal, DATA_CLASSIFICATION_ROLE?.pepsicoPublic].includes(order?.dataClassification?.toLowerCase()) && {
                accessRestrictions: order?.accessRestrictions,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            order?.dataClassification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            order?.isIdxAttributeRequired && {
                idxAttributeAvailable: order?.isIdxAttributeRequired === 'yes',
            }),
        ...((isSamlRequest || isOidcRequest) &&
            order?.dataClassification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            order?.isIdxAttributeRequired === 'yes' && {
                userIdxAttribute: order?.idxAttribute,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            order?.dataClassification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            order?.authorizationRolesRequired === 'yes' && {
                samlAssertionRolesRequired: order?.isSamlAssertionRolesRequired === 'yes',
            }),
        ...((isSamlRequest || isOidcRequest) &&
            order?.isIdxAttributeRequired === 'yes' &&
            order?.authorizationRolesRequired === 'yes' &&
            order?.isSamlAssertionRolesRequired === 'yes' && {
                userIdxRolesAttribute: order?.userIdxRolesAttribute,
            }),
        ...((isSamlRequest || isOidcRequest) && {
            applicationLogoRequired: order?.applicationLogoRequired === 'yes',
            isMFARequired: order?.isMFARequired === 'yes',
        }),
        ...(isM2MRequest && {
            serviceType: order?.serviceType || null,
            scopes: order?.customScopeName || [],
        }),
        ...(order?.disclaimerAcknowledged && {
            disclaimerAcknowledged: order?.disclaimerAcknowledged,
        }),
    };

    const newPayload = {
        ...payload,
        ...(order?.orderId && {
            deletedFiles: order?.deletedFiles || [],
        }),
    };
    const formData = new FormData();
    const blobPayload = converObjectToBlob(newPayload);
    formData.append('ssoRequest', blobPayload);
    if (uploadedFiles && uploadedFiles?.length) {
        uploadedFiles?.forEach((file) => {
            if (!order?.orderId) {
                formData.append('file', file);
            }
            if (!file?.blobRelativeLink && order?.orderId) {
                formData.append('file', file);
            }
        });
    } else {
        formData.append('file', null);
    }
    if (uploadedFiles && uploadedFiles?.length && uploadedFiles?.every((file) => file?.blobRelativeLink)) {
        formData.append('file', new Blob([''], { type: 'application/octet-stream' }), '');
    }
    return formData;
};

export const formUploadObjFromOrderDetails = (catalogServiceId = null, data = null) => {
    const catalog = data?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || null;
    const catalogConfig = catalog?.catalogConfig || null;

    return (
        catalogConfig?.filesUploadResponse?.uploadResponses?.map((item) => ({
            id: item?.id,
            name: item?.fileName,
            blobRelativeLink: item?.blobRelativeLink,
            size: item?.fileSize || 0,
        })) || []
    );
};

export const formOrderObjFromOrderDetails = (catalogServiceId = null, data = null) => {
    const catalog = data?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || null;
    const catalogConfig = catalog?.catalogConfig || null;
    const isSamlRequest = catalogConfig?.requestedType === SAMLRequestType;
    const isOidcRequest = catalogConfig?.requestedType === OIDCRequestType;
    const isM2MRequest = catalogConfig?.requestedType === MACHINERequestType;

    const isExternalRequest =
        (isSamlRequest || isOidcRequest) &&
        catalogConfig?.dataClasification &&
        catalogConfig?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal;

    const isExternalRequestWithoutIdx = isExternalRequest && !catalogConfig?.idxAttributeAvailable;

    return {
        requestType: catalogConfig?.requestedType,
        businessApplication: catalogConfig?.businessApplication,
        applicationCI: catalogConfig?.applicationCI,
        businessApplicationDetails: {
            label: catalogConfig?.businessApplication,
            value: catalogConfig?.businessApplication,
        },
        sector: catalogConfig?.sector,
        environment: catalogConfig?.environment === 'Pre-Prod' ? 'Pre-Production' : catalogConfig?.environment,
        ...((isSamlRequest || isOidcRequest) && {
            appType: catalogConfig?.applicationType,
            dataClassification: catalogConfig?.dataClasification,
        }),
        ...(isSamlRequest && {
            nameIdFormat: catalogConfig?.nameIdFormat,
            applicationUserName: catalogConfig?.applicationUserName,
        }),
        applicationName: catalogConfig?.applicationName,
        ...((isSamlRequest || isOidcRequest) && {
            applicationUrl: catalogConfig?.applicationURL,
        }),
        ...(isExternalRequest && {
            isIdxAccessControlRequired: catalogConfig?.isIdxAccessControlRequired ? 'yes' : 'no',
        }),
        ...(isExternalRequestWithoutIdx && { idxShortName: catalogConfig?.applicationShortName }),
        appOwner: null,
        appOwnerDetails: { name: data?.manager?.name, gpid: data?.manager?.id },
        appOwnerText: catalogConfig?.applicationOwner,
        ...(isM2MRequest &&
            catalogConfig?.environment === 'Production' &&
            catalogConfig?.ssoOwner && {
                ssoOwnerDetails: { gpid: catalogConfig?.ssoOwner, name: catalogConfig?.ssoOwner },
            }),
        authorizationRolesRequired: catalogConfig?.authorizationRolesRequired?.toLowerCase(),
        isCertificateAvailable: catalogConfig?.isCertificateAvailable?.toLowerCase(),
        ...(catalogConfig?.roles?.length && {
            roles: catalogConfig?.roles?.map((role) => {
                return { role };
            }),
        }),
        ...(isSamlRequest && catalogConfig?.certificate && { serviceProviderSigningCert: catalogConfig?.certificate }),
        ...(isSamlRequest && catalogConfig?.businessJustification && { businessJustification: catalogConfig?.businessJustification }),
        ...(isSamlRequest && {
            acsURL: catalogConfig?.acsURL,
            audienceURI: catalogConfig?.entityId,
        }),
        ...(isOidcRequest &&
            catalogConfig?.loginURL && {
                loginUrl: catalogConfig?.loginURL,
            }),
        ...(isOidcRequest &&
            catalogConfig?.logoutUrlAvailable && {
                isLogoutUrlRequired: catalogConfig?.logoutUrlAvailable ? 'yes' : 'no',
            }),
        ...(isOidcRequest &&
            catalogConfig?.logoutURL && {
                logoutUrl: catalogConfig?.logoutURL,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            [DATA_CLASSIFICATION_ROLE?.pepsicoInternal, DATA_CLASSIFICATION_ROLE?.pepsicoPublic].includes(catalogConfig?.dataClasification?.toLowerCase()) && {
                accessRestrictions: catalogConfig?.accessRestrictions,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalogConfig?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal && {
                isIdxAttributeRequired: catalogConfig?.idxAttributeAvailable ? 'yes' : 'no',
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalogConfig?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            catalogConfig?.idxAttributeAvailable && {
                idxAttribute: catalogConfig?.userIdxAttribute,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalogConfig?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            catalogConfig?.authorizationRolesRequired === 'Yes' && {
                isSamlAssertionRolesRequired: catalogConfig?.samlAssertionRolesRequired ? 'yes' : 'no',
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalogConfig?.idxAttributeAvailable &&
            catalogConfig?.authorizationRolesRequired === 'Yes' &&
            catalogConfig?.samlAssertionRolesRequired && {
                userIdxRolesAttribute: catalogConfig?.userIdxRolesAttribute,
            }),
        ...((isSamlRequest || isOidcRequest) && {
            applicationLogoRequired: catalogConfig?.applicationLogoRequired ? 'yes' : 'no',
            isMFARequired: catalogConfig?.isMFARequired ? 'yes' : 'no',
        }),
        ...(isM2MRequest && {
            serviceType: catalogConfig?.serviceType || null,
            customScopeName: catalogConfig?.scopes || [],
        }),
        isTouched: false,
    };
};

export const getSsoResubmitRequestPayload = ({ order }) => {
    const catalog = order?.catalogServices?.[0]?.catalogConfig;
    const isSamlRequest = catalog?.requestedType === SAMLRequestType;
    const isOidcRequest = catalog?.requestedType === OIDCRequestType;
    const isM2MRequest = catalog?.requestedType === MACHINERequestType;
    let businessJustificationValue = '';

    const isExternalRequest =
        (isSamlRequest || isOidcRequest) &&
        catalog?.dataClasification &&
        catalog?.dataClasification.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal;

    const isExternalRequestWithoutIdx = isExternalRequest && !catalog?.idxAttributeAvailable;
    if (isSamlRequest && SIGNATURE_ROLE_TO_SHOW?.includes(catalog?.dataClasification?.toLowerCase()) && order?.isCertificateAvailable === 'yes') {
        businessJustificationValue = { certificate: catalog?.serviceProviderSigningCert };
    } else if (isSamlRequest) {
        businessJustificationValue = { businessJustification: catalog?.businessJustification };
    }

    const payload = {
        requestedType: catalog?.requestedType,
        businessApplication: order?.businessApplication,
        applicationCI: order?.applicationCI,
        sector: catalog?.sector,
        environment: catalog?.environment === 'Pre-Production' ? 'Pre-Prod' : catalog?.environment,
        ...((isSamlRequest || isOidcRequest) && { applicationType: catalog?.applicationType, dataClasification: catalog?.dataClasification }),
        ...(catalog?.roles?.length && {
            roles: catalog?.roles,
        }),
        applicationName: catalog?.applicationName?.trim(),
        ...((isSamlRequest || isOidcRequest) && { applicationURL: catalog?.applicationURL }),
        ...(isExternalRequest && { isIdxAccessControlRequired: catalog?.isIdxAccessControlRequired === 'yes' }),
        ...(isExternalRequestWithoutIdx && {
            applicationShortName: catalog?.applicationShortName,
        }),
        applicationOwner: catalog?.applicationOwner,
        ...(isM2MRequest && catalog?.environment === 'Production' && { ssoOwner: catalog?.ssoOwner || null }),
        authorizationRolesRequired: titleCase(catalog?.authorizationRolesRequired || 'no'),
        isCertificateAvailable: titleCase(catalog?.isCertificateAvailable || 'no'),
        ...(catalog?.requestedType === SAMLRequestType && {
            acsURL: catalog?.acsURL,
            entityId: catalog?.entityId,
            nameIdFormat: catalog?.nameIdFormat,
            applicationUserName: catalog?.applicationUserName,
        }),
        ...(isOidcRequest &&
            catalog?.loginURL && {
                loginUrl: catalog?.logoutURL,
            }),
        ...(isOidcRequest &&
            catalog?.logoutUrlAvailable && {
                isLogoutUrlRequired: catalog?.logoutUrlAvailable ? 'yes' : 'no',
            }),
        ...(isOidcRequest &&
            catalog?.logoutURL && {
                logoutUrl: catalog?.logoutURL,
            }),
        ...(isSamlRequest &&
            catalog?.certificate &&
            catalog?.entityId &&
            catalog?.isCertificateAvailable === 'yes' && {
                certificate: catalog?.certificate,
            }),
        ...businessJustificationValue,
        ...((isSamlRequest || isOidcRequest) &&
            [DATA_CLASSIFICATION_ROLE?.pepsicoInternal, DATA_CLASSIFICATION_ROLE?.pepsicoPublic].includes(catalog?.dataClasification?.toLowerCase()) && {
                accessRestrictions: catalog?.accessRestrictions,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalog?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            catalog?.idxAttributeAvailable && {
                isIdxAttributeRequired: catalog?.idxAttributeAvailable ? 'yes' : 'no',
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalog?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            catalog?.idxAttributeAvailable && {
                idxAttribute: catalog?.userIdxAttribute,
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalog?.dataClasification?.toLowerCase() !== DATA_CLASSIFICATION_ROLE?.pepsicoInternal &&
            catalog?.authorizationRolesRequired === 'Yes' && {
                isSamlAssertionRolesRequired: catalog?.samlAssertionRolesRequired ? 'yes' : 'no',
            }),
        ...((isSamlRequest || isOidcRequest) &&
            catalog?.idxAttributeAvailable &&
            catalog?.authorizationRolesRequired === 'Yes' &&
            catalog?.samlAssertionRolesRequired && {
                userIdxRolesAttribute: catalog?.userIdxRolesAttribute,
            }),
        ...((isSamlRequest || isOidcRequest) && {
            applicationLogoRequired: catalog?.applicationLogoRequired ? 'yes' : 'no',
            isMFARequired: catalog?.isMFARequired ? 'yes' : 'no',
        }),
        ...(isM2MRequest && {
            serviceType: catalog?.serviceType || null,
            customScopeName: catalog?.customScopeName || [],
        }),
    };
    const newPaylod = { ...payload, deletedFiles: [], newFiles: [] };
    return newPaylod;
};

export const resetOrder = {
    catalogCode: null,
    applicationCI: null,
    sector: null,
    environment: null,
    envShortName: null,
    businessApplication: null,
    businessApplicationDetails: null,
    appType: null,
    dataClassification: null,
    isCertificateAvailable: 'no',
    applicationName: null,
    applicationUrl: null,
    loginUrl: [],
    logoutUrl: [],
    idxShortName: null,
    appOwner: null,
    appOwnerDetails: null,
    requestType: null,
    destinationUrl: null,
    recipientUrl: null,
    acsURL: null,
    appOwnerText: null,
    audienceURI: null,
    serviceProviderSigningCert: null,
    businessJustification: null,
    isTouched: false,
    nameIdFormat: null,
    applicationUserName: null,
    accessRestrictions: [],
    isIdxAccessControlRequired: 'no',
    isIdxAttributeRequired: 'no',
    idxAttribute: null,
    isSamlAssertionRolesRequired: 'no',
    userIdxRolesAttribute: null,
    applicationLogoRequired: 'no',
    isMFARequired: 'no',
    serviceType: null,
    scopes: [],
    ssoOwner: null,
    ssoOwnerDetails: null,
    disclaimerAcknowledged: false,
    deletedFiles: [],
};

export const commonRegex =
    /^(https?:\/\/)(?!.*(?:secure\.pepsico|secure\.mypepsico|secure\.ite\.pepsico|secure\.ite\.mypepsico|secure\.it\.pepsico|secure\.it\.mypepsico|okta|oktapreview|oktadebugger|oidcdebugger|localhost))([a-zA-Z0-9\-_:/.?=]+)$/i;

export const urlConfig = {
    common: {
        regex: commonRegex,
        message: `URL is Invalid.`,
    },
    production: {
        regex: commonRegex,
        message: `URL is Invalid.`,
    },
    applicationURL: {
        regex: commonRegex,
        message: `URL is Invalid.`,
    },
};

export const validateLoginLogoutURL = (url, environment, requestType) => {
    let config;

    if (requestType === OIDCRequestType) {
        config = urlConfig?.common;
    } else if (requestType === SAMLRequestType && environment?.toLowerCase() === 'production') {
        config = urlConfig?.production;
    } else {
        config = urlConfig?.applicationURL;
    }

    if (!config) {
        throw new Error(`Invalid environment: ${environment}`);
    }

    const isValid = url !== null && url !== '' ? config.regex.test(url) : true;

    return {
        isValid,
        message: isValid ? 'Valid URL' : config.message,
    };
};

export const audienceURIRegX =
    /^(?!.*\b(?:secure\.pepsico|secure\.mypepsico|secure\.ite\.pepsico|secure\.ite\.mypepsico|secure\.it\.pepsico|secure\.it\.mypepsico|okta|oktadebugger|oktapreview|localhost|oidcdebugger)\b)([^\s"<>]+(:\d+)?(\/[^\s"<>]*)?$|^com[:/.].*)$/i;
